import type { ReactNode } from 'react';
import type { QuizHardestQuestionDto } from '@peach/types/quizzes/quiz-attempt.dto.js';

/**
 * At most three misses are listed: a result card is a reason to talk, not a
 * report card.
 */
const MISSED_SHOWN = 3;

/**
 * The panel under the score: what the player got wrong, with the right answer only
 * for a member of the creator (the server sends it to nobody else; for everyone
 * else the answers are what the call is for), or, when they got
 * nothing wrong, what OTHER people miss. "Everything's fine" is the one thing
 * this panel must never say: it ends the conversation, which is the opposite
 * of what a quiz on a storefront is for. The plain "clean run" line is the
 * last resort, for a quiz too young to have a cohort.
 *
 * `translate="no"` on the question text: a browser translation of a language
 * quiz rewrites the very words being tested.
 */
export function QuizReview({
  missed,
  hardest = [],
}: {
  /** `answer` is null unless the player is a member: then it is the right option. */
  missed: readonly { text: string; answer: string | null }[];
  /** What other players get wrong — only ever used when `missed` is empty. */
  hardest?: readonly QuizHardestQuestionDto[];
}): ReactNode {
  const shown = missed.slice(0, MISSED_SHOWN);
  const others = shown.length === 0 ? hardest.slice(0, MISSED_SHOWN) : [];
  const clean = shown.length === 0 && others.length === 0;

  return (
    <div className="rounded-[10px] bg-black/30 p-3">
      <span className="mb-1.5 block text-badge font-bold uppercase tracking-wider">
        {shown.length > 0
          ? 'Worth going over'
          : others.length > 0
            ? 'Where everyone else slips'
            : 'Clean run'}
      </span>

      {clean ? (
        <span className="text-p-3">You didn&rsquo;t miss one.</span>
      ) : others.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {others.map((question, at) => (
            // Keyed by position, like every list on a quiz: nothing stops an
            // author writing the same question twice.
            <li key={at} className="flex gap-2 text-p-3">
              {/* The number leads: this is a fact about the crowd, not a mark
                  against the reader. */}
              <span className="sf-num shrink-0 font-extrabold tabular-nums">
                {question.missPercent}%
              </span>
              <span translate="no" className="sf-sealed notranslate">
                {question.text}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col gap-1">
          {shown.map(({ text, answer }, at) => (
            <li key={at} className="flex gap-2 text-p-3">
              <span aria-hidden className="shrink-0 font-bold">
                ✕
              </span>
              <span translate="no" className="sf-sealed notranslate">
                {text}
                {answer && (
                  <span className="block font-bold">
                    <span className="sr-only">Right answer: </span>→ {answer}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
